// errorReporter is the console SPA's single funnel for unexpected
// client-side failures: React render throws (caught by ErrorBoundary)
// and uncaught window-level errors / unhandled promise rejections.
//
// Today it emits a structured console.error line — the same posture as
// the Go services' structured logs (CLAUDE.md §12). The sink is
// pluggable so a future external reporter (Sentry, Cloud Logging via a
// beacon endpoint, etc.) can be wired in without touching every call
// site: swap the active sink via setErrorSink.

// ReportedError is the structured shape every report carries. Keys are
// stable so a future log-based query can match on them.
export interface ReportedError {
  event: 'console_error'
  source: 'react' | 'window.onerror' | 'unhandledrejection'
  message: string
  stack?: string
  // componentStack is only present for React render errors caught by the
  // ErrorBoundary.
  componentStack?: string
}

// ErrorSink consumes a structured report. Implementations must not throw
// (a throwing sink during error handling would mask the original fault).
export type ErrorSink = (report: ReportedError) => void

// consoleSink is the default v1 sink: a single structured console.error
// line. console.error (not warn) so it surfaces in browser devtools and
// any console-capturing harness.
const consoleSink: ErrorSink = (report) => {
  console.error('[backlit]', report)
}

let activeSink: ErrorSink = consoleSink

// setErrorSink swaps the active sink. Returns the previous sink so a
// test (or a future reporter that wants to chain) can restore it.
export function setErrorSink(sink: ErrorSink): ErrorSink {
  const prev = activeSink
  activeSink = sink
  return prev
}

// toMessage coerces an unknown thrown value into a human string without
// throwing itself (a thrown non-Error, e.g. a string or object, is
// common in the wild).
function toMessage(err: unknown): string {
  if (err instanceof Error) return err.message
  if (typeof err === 'string') return err
  try {
    return JSON.stringify(err)
  } catch {
    return String(err)
  }
}

function toStack(err: unknown): string | undefined {
  return err instanceof Error ? err.stack : undefined
}

// reportError emits a structured report through the active sink. Safe to
// call from inside an error handler: it swallows any sink failure so it
// can never mask the original problem.
export function reportError(
  err: unknown,
  source: ReportedError['source'],
  extra?: { componentStack?: string },
): void {
  // Build the report omitting absent optional fields rather than setting
  // them to undefined — the tsconfig runs exactOptionalPropertyTypes.
  const report: ReportedError = {
    event: 'console_error',
    source,
    message: toMessage(err),
  }
  const stack = toStack(err)
  if (stack !== undefined) report.stack = stack
  if (extra?.componentStack !== undefined) report.componentStack = extra.componentStack
  try {
    activeSink(report)
  } catch {
    // A sink must not be able to break error handling. Intentionally
    // empty — there is nowhere safe left to report to.
  }
}

// installGlobalErrorHandlers wires window.onerror + onunhandledrejection
// to reportError. Idempotent: calling twice does not double-register.
// Returns a teardown function (used by tests; production never unwires).
export function installGlobalErrorHandlers(target: Window = window): () => void {
  const onError = (event: ErrorEvent) => {
    reportError(event.error ?? event.message, 'window.onerror')
  }
  const onRejection = (event: PromiseRejectionEvent) => {
    reportError(event.reason, 'unhandledrejection')
  }
  target.addEventListener('error', onError)
  target.addEventListener('unhandledrejection', onRejection)
  return () => {
    target.removeEventListener('error', onError)
    target.removeEventListener('unhandledrejection', onRejection)
  }
}
